<style scoped>
/* The panel is a flex column: header + scrollable body + sticky player bar.
   The floating panel already draws the frame, so the card chrome is dropped —
   the `card` class stays for its padding variables (--bs-card-spacer-*). The
   shadow needs !important: Bootstrap's .shadow-sm utility is itself
   !important, so a plain declaration would lose to it. */
.review-panel {
	display: flex;
	flex-direction: column;
	border: none;
	border-radius: 0;
	box-shadow: none !important;
}

.review-panel .review-stack {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
}

.review-panel .review-scroll {
	flex: 1 1 auto;
	overflow-y: auto;
	min-height: 0;
	/* Top padding removed so the sticky player bar sits flush at the top of
	   the panel; the bar's own margin restores the spacing to the content. */
	padding-top: 0;
}

.review-panel .pane-header {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: flex-start;
	gap: 0.75rem;
	font-weight: 600;
	flex-shrink: 0;
}

/* At panel width the metadata block takes the whole row and the toolbar drops
   to its own below it. The toolbar is allowed to shrink so its icon buttons
   wrap onto a third row rather than push the header past the panel width. */
.pane-title {
	flex: 1 1 100%;
	min-width: 0;
}

.pane-header :deep(.review-toolbar) {
	flex-wrap: wrap;
	flex-shrink: 1;
}

.record-title {
	margin-bottom: 0.25rem;
	font-weight: 600;
}

.header-meta {
	font-size: 0.875rem;
	color: #555;
}

.section-title {
	font-size: 0.75rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	color: #888;
	margin: 0 0 0.5rem;
}

/* Notes under the player bar (audio failure / mutation errors). */
.player-note {
	font-size: 0.8125rem;
	margin-bottom: 0.5rem;
}

.player-note.error {
	color: #b02a37;
}

.player-note.notice {
	color: #146c43;
}

/* TRANSCRIPT */
.transcript-lines {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	max-height: 14rem;
	overflow-y: auto;
}

.transcript-line {
	display: flex;
	gap: 0.75rem;
	align-items: flex-start;
	text-align: left;
	background: none;
	border: 1px solid transparent;
	border-radius: 4px;
	padding: 0.25rem 0.5rem;
	cursor: pointer;
	width: 100%;
}

.transcript-line:hover {
	background: #f1f3f5;
}

/* The line being spoken during playback. */
.transcript-line.active {
	background: #cfe2ff;
	border-color: #9ec5fe;
}

.transcript-line.editing {
	background: #f8f9ff;
	border-color: #9ec5fe;
}

.transcript-line .line-time {
	font-weight: 600;
	color: #555;
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
	padding-top: 0.25rem;
}

.transcript-line .line-text {
	color: #333;
	flex: 1;
}

.transcript-line .line-original {
	color: #888;
	text-decoration: line-through;
	margin-left: 0.375rem;
}

.transcript-line .edited-badge {
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: #614100;
	background: #ffe69c;
	border-radius: 4px;
	padding: 0.125rem 0.375rem;
	flex-shrink: 0;
	margin-top: 0.125rem;
}

/* The editor sits beside the timestamp in a non-wrapping row: let the textarea
   shrink below its intrinsic width rather than overflow the line at panel
   width. */
.transcript-line .line-edit-input {
	flex: 1;
	min-width: 0;
	font-size: 0.875rem;
}

/* Word-level playback highlight (karaoke). */
.transcript-line .transcript-word {
	border-radius: 2px;
	padding: 0 0.0625rem;
	cursor: pointer;
}

.transcript-line .transcript-word + .transcript-word::before {
	content: " ";
}

.transcript-line .transcript-word:hover {
	background: #e9ecef;
}

.transcript-line .transcript-word.word-active {
	background: #ffd43b;
	color: #212529;
}

/* EXTRACTION */
/* The title (kind, summary, ref, flags) takes the row it needs; the two
   action buttons drop to their own row at panel width instead of overflowing. */
.extraction-card .card-header {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	gap: 0.5rem;
}

.extraction-title {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.extraction-kind {
	font-weight: 700;
}

.extraction-summary {
	color: #555;
}

.transcript-ref {
	font-size: 0.75rem;
	font-weight: 600;
	color: #555;
	background: #e9ecef;
	border-radius: 4px;
	padding: 0.125rem 0.375rem;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	cursor: pointer;
}

.transcript-ref:hover {
	background: #dee2e6;
}

.extraction-actions {
	display: flex;
	gap: 0.25rem;
	flex-shrink: 0;
}

/* Server-derived records — the server's own re-derivation from the note's
   words, read-only evidence rather than a review object: the dashed left rule
   and the muted, letter-spaced sub-heading set them apart from the
   reviewer-actionable cards above (the layers-* read-only idiom). */
.derived-heading {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: #888;
}

.derived-note {
	margin-top: 0.125rem;
	margin-bottom: 0.375rem;
}

.derived-card {
	border-left: 3px dashed #dee2e6;
}

/* These cards carry no action row, so mute their header too: the dashed rule
   and the missing buttons read as one signal. */
.derived-card .card-header {
	background: #f8f9fa;
}

/* Correction editor (per-record ✎ Correct → POST extraction, supersede). */
.correction-field {
	margin-bottom: 0.5rem;
}

.correction-field .form-label {
	font-size: 0.8125rem;
	color: #555;
	margin-bottom: 0.125rem;
}

.correction-field .field-unit {
	color: #888;
}

/* The value row (input + unit + "(derived)") does not wrap: let the input give
   the width up instead of pushing the row past the card at panel width. */
.correction-field .form-control {
	min-width: 0;
}

/* Comment composer (per-record 💬 Comment → POST review). */
.comment-box {
	border-top: 1px dashed #dee2e6;
	padding-top: 0.5rem;
	margin-top: 0.5rem;
}

/* NLU LAYERS — the device's derived-layer verdict + drift evidence. */
.layers-header {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.layers-version {
	font-weight: 600;
	color: #555;
}

/* The device's vocabulary digest is a long opaque token: monospace so it can
   be eyeballed against a device build, truncated so it cannot wrap the row. */
.layers-vocabulary {
	font-family: "SFMono-Regular", Menlo, Consolas, monospace;
	font-size: 0.75rem;
	color: #888;
	background: #f1f3f5;
	border-radius: 4px;
	padding: 0.125rem 0.375rem;
	max-width: 14rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.layers-derived {
	font-size: 0.8125rem;
	color: #888;
}

/* The server's one-line "device …, server …" account of the disagreement. */
.layers-drift-summary {
	margin-top: 0.375rem;
	color: #b02a37;
}

.layers-drift {
	margin-top: 0.125rem;
}

/* The device session tree: collapsed by default — the verdict is the review
   surface, the tree is the evidence behind it. Rows indent by depth via an
   inline padding-left; read-only, no interaction beyond the toggle. */
.layers-tree {
	margin-top: 0.5rem;
	font-size: 0.8125rem;
}

.layers-tree > summary {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: #888;
	cursor: pointer;
}

.layers-tree-row {
	padding-top: 0.125rem;
}

.layers-tree-node {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 0.375rem;
}

.layers-tree-kind {
	font-weight: 600;
	color: #555;
}

.layers-tree-label {
	color: #333;
}

/* Node state pill — same treatment as the transcript's "edited" badge. */
.layers-tree-state {
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: #614100;
	background: #ffe69c;
	border-radius: 4px;
	padding: 0.0625rem 0.25rem;
}

.layers-tree-span {
	color: #888;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

/* Attribute sub-rows sit one step in from their node. */
.layers-tree-attribute {
	display: flex;
	flex-wrap: wrap;
	gap: 0.375rem;
	padding-left: 0.75rem;
}

/* Corrections: the technician's reading replaces the engine's as the primary
   value, with the engine's own struck through beside it (the transcript-line
   edit convention). Both explicit colors need !important — Bootstrap's
   .text-muted utility on the sub-row is itself !important — while the badge is
   left to inherit that same muted color. */
.layers-tree-attribute .line-original {
	color: #888 !important;
	text-decoration: line-through;
}

.layers-corrected-value {
	color: #333 !important;
}

span.layers-correction-badge {
	font-size: 0.6875rem;
	font-weight: 600;
	letter-spacing: 0.04em;
}

/* PHOTO VIEW (inline) */
.photo-view {
	border: 1px solid #dee2e6;
	border-radius: 4px;
	background: #f8f9fa;
	padding: 0.5rem;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	align-items: flex-start;
}

.photo-view-img {
	max-width: 100%;
	max-height: 16rem;
	border-radius: 4px;
	border: 1px solid #dee2e6;
}

.photo-view-meta {
	font-size: 0.8125rem;
	color: #555;
}

.photo-view-meta .photo-segment {
	font-style: italic;
}

/* PLAYER BAR — sticky at the top of the panel: play/pause + progress track +
   time readout + speed select + volume. The transcript/extraction scroll under
   it, so it stays visible while reviewing. At panel width the transport and the
   settings do not fit on one line, so the bar stacks them: the transport row
   (play/pause, marker/track, time readout) above the settings row (speed,
   mute, volume). */
.player-bar {
	position: sticky;
	top: 0;
	z-index: 3;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.625rem 0.75rem;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
	flex-shrink: 0;
	/* Bleed over the card-body side padding so the bar spans the panel width;
	   the card-body's top padding is removed (see .review-scroll) so the bar
	   sits flush; this margin restores the spacing to the content below. */
	margin: 0 calc(-1 * var(--bs-card-spacer-x)) var(--bs-card-spacer-y);
}

.player-main,
.player-controls {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.player-toggle {
	flex-shrink: 0;
	line-height: 1;
}

.player-track-wrap {
	position: relative;
	flex: 1;
	min-width: 0;
}

/* MARKER ROW — photo 📷 / location ● icons float ABOVE the track at their
   timestamp x-positions. Each icon is its own click target (photo view / map
   view), kept off the track so scrubbing the track stays unobstructed. */
.marker-row {
	position: relative;
	height: 1.25rem;
}

.marker-icon {
	position: absolute;
	top: 50%;
	transform: translate(-50%, -50%);
	z-index: 2;
	cursor: pointer;
	background: none;
	border: none;
	padding: 0 0.125rem;
	line-height: 1;
	font-size: 0.8125rem;
	user-select: none;
}

.marker-icon.loc {
	color: #0d6efd;
	font-size: 1rem;
}

.marker-icon.photo {
	font-size: 0.9375rem;
}

.player-track {
	position: relative;
	width: 100%;
	height: 1.5rem;
	cursor: pointer;
	touch-action: none;
}

.track-base {
	position: absolute;
	left: 0;
	right: 0;
	top: 50%;
	height: 0.375rem;
	background: #e9ecef;
	border-radius: 4px;
	transform: translateY(-50%);
}

.track-fill {
	position: absolute;
	left: 0;
	top: 50%;
	height: 0.375rem;
	background: #0d6efd;
	border-radius: 4px;
	transform: translateY(-50%);
}

.track-playhead {
	position: absolute;
	top: 50%;
	width: 0.875rem;
	height: 0.875rem;
	background: #0d6efd;
	border: 2px solid #fff;
	border-radius: 50%;
	box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.4);
	transform: translate(-50%, -50%);
	z-index: 1;
	pointer-events: none;
}

.player-time {
	font-size: 0.875rem;
	color: #555;
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
	white-space: nowrap;
}

.player-speed {
	width: 4.5rem;
	flex-shrink: 0;
}

.player-mute {
	flex-shrink: 0;
	line-height: 1;
}

/* The volume slider fills whatever the speed select and mute button leave on
   the settings row (no fixed width, so the row cannot overflow the panel). */
.player-volume {
	flex: 1 1 auto;
	width: auto;
	min-width: 0;
}
/* Full-screen modal holding the extraction records and the device's NLU
   layers — opened from the icon beside the TRANSCRIPT header. Teleported to
   <body> so it covers the whole viewport, panels included. */
.analysis-open {
	padding: 0.05rem 0.4rem;
	line-height: 1;
	color: #6c757d;
}

.analysis-backdrop {
	position: fixed;
	inset: 0;
	z-index: 2000;
	background: rgba(33, 37, 41, 0.5);
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 4vh 1rem;
	overflow-y: auto;
}

.analysis-modal {
	width: 100%;
	max-width: 720px;
	max-height: 92vh;
	display: flex;
	flex-direction: column;
}

.analysis-modal-body {
	padding: 1rem 1rem 0.5rem;
	overflow-y: auto;
}

/* DERIVED RESOURCES — the ontology records this note's derivation created or
   matched. Only site/feature/inspection/treatment link to a review page; the
   other kinds are labeled text, so the row carries both shapes. */
.derived-resource-list {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.derived-resource-row {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.8125rem;
}

.derived-resource-link {
	font-weight: 600;
}

.derived-resource-kind {
	font-weight: 600;
	color: #555;
}

.derived-resource-id {
	color: #888;
	font-variant-numeric: tabular-nums;
}
</style>

<template>
	<div class="card shadow-sm h-100 review-panel">
		<div v-if="!transcript" class="card-body text-muted">
			{{ emptyMessage }}
		</div>
		<div v-else class="review-stack">
			<!-- Real playback source: GET /api/transcript/{id}/audio (Range
			     streamed by the server). Hidden — the custom player bar below
			     drives play/pause/seek/speed/volume on this element. -->
			<audio
				ref="audioEl"
				:src="audioUrl"
				class="d-none"
				preload="metadata"
				@timeupdate="onAudioTime"
				@loadedmetadata="onAudioMetadata"
				@durationchange="onAudioMetadata"
				@play="isPlaying = true"
				@pause="isPlaying = false"
				@ended="onAudioEnded"
				@error="onAudioError"
			></audio>

			<!-- Header — transcript metadata + note-level actions toolbar -->
			<div class="card-header bg-white pane-header">
				<div class="pane-title">
					<h5 class="record-title">
						{{ transcriptTitle(transcript) }}
						<span
							:class="statusBadgeClass(transcript.status)"
							class="badge align-middle ms-1"
						>
							{{ transcript.status }}
						</span>
					</h5>
					<div class="header-meta">
						<div>
							User: <strong>{{ transcript.technician }}</strong> · Survey:
							<strong>{{ transcript.surveyId }}</strong> · Assignment:
							<strong>{{ transcript.assignment }}</strong>
						</div>
						<div>
							Route: {{ transcript.route }} ·
							{{ formatTimestamp(transcript.timestamp) }} ·
							{{ transcript.location }}
						</div>
					</div>
				</div>
				<ReviewTranscriptToolbar
					:status="transcript.status"
					:busy="busy"
					@comment="commentOnNote"
					@clarify="clarifyNote"
					@status-change="updateStatus"
				/>
			</div>

			<div class="card-body review-scroll">
				<!-- PLAYER BAR — sticky at the top of the panel; transcript and
				     extraction scroll under it -->
				<div class="player-bar">
					<div class="player-main">
						<button
							class="btn btn-sm btn-outline-primary player-toggle"
							:title="isPlaying ? 'Pause' : 'Play'"
							:disabled="audioError !== null"
							@click="togglePlayback"
						>
							{{ isPlaying ? "⏸" : "▶" }}
						</button>
						<div class="player-track-wrap">
							<!-- MARKER ROW — photo 📷 / location ● icons float ABOVE the
							     track at their timestamps; each is its own click target
							     (photo view / map view), kept off the track so scrubbing
							     is unobstructed -->
							<div class="marker-row">
								<span
									v-for="(event, index) in timelineEvents"
									:key="index"
									class="marker-icon"
									:class="event.kind"
									:style="{ left: markerLeft(event.time) }"
									:title="markerTitle(event)"
									@click.stop="openTimelineEvent(event)"
									>{{ event.kind === "photo" ? "📷" : "●" }}</span
								>
							</div>
							<div
								ref="trackEl"
								class="player-track"
								role="slider"
								aria-label="Playback timeline"
								:aria-valuemin="0"
								:aria-valuemax="playerDuration"
								:aria-valuenow="playbackTime.toFixed(1)"
								@pointerdown="startScrub"
								@pointermove="scrubMove"
								@pointerup="endScrub"
								@pointercancel="endScrub"
							>
								<div class="track-base"></div>
								<div
									class="track-fill"
									:style="{ width: playheadPercent }"
								></div>
								<div
									class="track-playhead"
									:style="{ left: playheadPercent }"
								></div>
							</div>
						</div>
						<span class="player-time">
							{{ formatTranscriptDuration(playbackTime) }} /
							{{ formatTranscriptDuration(playerDuration) }}
						</span>
					</div>

					<div class="player-controls">
						<!-- PLAYBACK SPEED — applied to the <audio> element -->
						<select
							v-model.number="playbackSpeed"
							class="form-select form-select-sm player-speed"
							title="Playback speed"
							aria-label="Playback speed"
						>
							<option
								v-for="speed in playbackSpeeds"
								:key="speed"
								:value="speed"
							>
								{{ speed }}×
							</option>
						</select>

						<!-- VOLUME — mute toggle + slider on the audio element -->
						<button
							class="btn btn-sm btn-outline-secondary player-mute"
							type="button"
							:title="muted ? 'Unmute' : 'Mute'"
							aria-label="Mute"
							@click="toggleMute"
						>
							<i class="bi" :class="volumeIcon"></i>
						</button>
						<input
							class="form-range player-volume"
							type="range"
							min="0"
							max="1"
							step="0.05"
							:value="volume"
							title="Volume"
							aria-label="Volume"
							@input="
								setVolume(Number(($event.target as HTMLInputElement).value))
							"
						/>
					</div>
				</div>

				<div v-if="audioError" class="player-note error">
					{{ audioError }}
					<button
						class="btn btn-sm btn-link p-0 align-baseline"
						type="button"
						@click="retryAudio"
					>
						Retry
					</button>
				</div>
				<div v-if="mutationError" class="player-note error">
					{{ mutationError }}
				</div>
				<div v-if="mutationNotice" class="player-note notice">
					{{ mutationNotice }}
				</div>

				<!-- TRANSCRIPT — server transcription, timestamped lines -->
				<div>
					<div class="section-title d-flex align-items-center gap-2">
						<span>TRANSCRIPT</span>
						<button
							class="btn btn-sm btn-outline-secondary analysis-open"
							type="button"
							title="Show extraction & NLU layers"
							aria-label="Show extraction and NLU layers"
							@click="analysisOpen = true"
						>
							<i class="bi bi-diagram-3"></i>
						</button>
					</div>
					<div class="transcript-lines">
						<div
							v-for="(lineView, index) in transcriptLineViews"
							:key="lineView.id ?? `legacy-${index}`"
							class="transcript-line"
							:class="{
								active: isPlaybackLine(index),
								editing: editingLineIndex === index,
							}"
							role="button"
							:title="lineTitle(lineView, index)"
							@click="startLineEdit(index)"
						>
							<span class="line-time">
								{{ formatTranscriptDuration(lineView.startSeconds) }}
							</span>
							<template v-if="editingLineIndex === index">
								<textarea
									v-model="editDraft"
									:ref="setEditInput"
									class="form-control form-control-sm line-edit-input"
									rows="2"
									:disabled="savingLine"
									@click.stop
									@keydown.enter.exact.prevent="saveLineEdit(index)"
									@keydown.esc.prevent="cancelLineEdit"
								></textarea>
								<span v-if="savingLine" class="text-muted small">Saving…</span>
							</template>
							<template v-else>
								<span class="line-text">
									<template v-if="lineView.original">
										"{{ lineView.text }}"
										<s class="line-original">{{ lineView.original }}</s>
									</template>
									<template v-else-if="lineView.words.length > 0">
										<span>"</span>
										<template
											v-for="(word, wordIndex) in lineView.words"
											:key="wordIndex"
										>
											<span
												class="transcript-word"
												:class="{
													'word-active': isActiveWord(index, wordIndex),
												}"
												:title="
													'Seek to ' + formatTranscriptDuration(word.start)
												"
												@click.stop="seekTo(word.start)"
												>{{ word.text }}</span
											>
										</template>
										<span>"</span>
									</template>
									<span v-else>"{{ lineView.text }}"</span>
								</span>
								<span v-if="lineView.original" class="edited-badge"
									>edited</span
								>
							</template>
						</div>
						<div
							v-if="transcriptLineViews.length === 0"
							class="text-muted small px-1 py-1"
						>
							No timestamped transcript for this recording.
						</div>
					</div>
				</div>

				<!-- EXTRACTION + NLU LAYERS — moved off the detail pane into a
				     full-screen modal opened from the icon beside the TRANSCRIPT
				     header, so the pane keeps only the transcript and audio. -->
				<Teleport to="body">
					<div
						v-if="analysisOpen"
						class="analysis-backdrop"
						@click.self="analysisOpen = false"
					>
						<div
							class="analysis-modal card"
							role="dialog"
							aria-modal="true"
							aria-label="Extraction and NLU layers"
						>
							<div class="card-header bg-light pane-header">
								<span class="fw-semibold">Extraction &amp; NLU layers</span>
								<button
									class="btn btn-sm btn-outline-secondary ms-auto"
									type="button"
									aria-label="Close extraction and NLU layers"
									@click="analysisOpen = false"
								>
									<i class="bi bi-x-lg"></i>
								</button>
							</div>
							<div class="analysis-modal-body">
								<!-- EXTRACTION — proposed Type 2 records, in order -->
								<div class="mt-3">
									<div class="section-title">EXTRACTION</div>
									<div
										v-if="
											transcript.extractions.length === 0 &&
											transcript.derivedExtractions.length === 0
										"
										class="text-muted small"
									>
										No extraction records for this recording yet.
									</div>
									<div
										v-for="extraction in transcript.extractions"
										:key="extraction.id"
										class="card extraction-card mb-2"
									>
										<div class="card-header bg-white py-2">
											<div class="extraction-title">
												<span class="extraction-kind">
													{{ extraction.label }}
												</span>
												<span class="extraction-summary">
													{{ extraction.summary }}
												</span>
												<span
													v-if="extraction.transcriptRef"
													class="transcript-ref"
													title="Seek to this transcript segment"
													@click.stop="
														seekTo(extraction.transcriptRef.startSeconds)
													"
												>
													[{{
														formatTranscriptDuration(
															extraction.transcriptRef.startSeconds,
														)
													}}–{{
														formatTranscriptDuration(
															extraction.transcriptRef.endSeconds,
														)
													}}]
												</span>
												<span
													v-for="flag in extraction.flags"
													:key="flag.label"
													:class="flagBadgeClass(flag.kind)"
													class="badge ms-1"
												>
													⚠ {{ flag.label }}
												</span>
											</div>
											<div class="extraction-actions">
												<button
													class="btn btn-sm btn-outline-secondary"
													type="button"
													:disabled="busy"
													@click="openCorrection(extraction)"
												>
													✎ Correct
												</button>
												<button
													class="btn btn-sm btn-outline-secondary"
													type="button"
													:disabled="busy"
													@click="openComment(extraction)"
												>
													💬 Comment
												</button>
											</div>
										</div>
										<div class="card-body py-2">
											<template v-if="correctingExtractionId === extraction.id">
												<input
													v-model="correctionSummary"
													class="form-control form-control-sm mb-2"
													:aria-label="'Summary for ' + extraction.label"
													placeholder="Summary"
												/>
												<div
													v-for="field in extraction.fields"
													:key="field.key"
													class="correction-field"
												>
													<label class="form-label d-block">{{
														field.label
													}}</label>
													<div class="d-flex align-items-center gap-2">
														<input
															class="form-control form-control-sm"
															:value="correctionFieldValue(field.key)"
															:disabled="field.derived || savingExtraction"
															@input="
																setCorrectionFieldValue(
																	field.key,
																	($event.target as HTMLInputElement).value,
																)
															"
														/>
														<span v-if="field.unit" class="field-unit small">{{
															field.unit
														}}</span>
														<span
															v-if="field.derived"
															class="field-unit small"
															title="Recomputed server-side — not editable"
															>(derived)</span
														>
													</div>
												</div>
												<div class="d-flex gap-2">
													<button
														class="btn btn-sm btn-primary"
														type="button"
														:disabled="savingExtraction"
														@click="saveCorrection(extraction)"
													>
														{{
															savingExtraction ? "Saving…" : "Save correction"
														}}
													</button>
													<button
														class="btn btn-sm btn-outline-secondary"
														type="button"
														:disabled="savingExtraction"
														@click="closeCorrection"
													>
														Cancel
													</button>
												</div>
											</template>
											<PropertyList v-else :entries="extraction.fields" />
											<div
												v-if="commentingExtractionId === extraction.id"
												class="comment-box"
											>
												<textarea
													v-model="commentDraft"
													class="form-control form-control-sm"
													rows="2"
													:disabled="savingComment"
													placeholder="Comment on this record…"
												></textarea>
												<div class="d-flex gap-2 mt-2">
													<button
														class="btn btn-sm btn-outline-secondary"
														type="button"
														:disabled="savingComment || !commentDraft.trim()"
														@click="saveComment(extraction)"
													>
														{{ savingComment ? "Saving…" : "Save comment" }}
													</button>
													<button
														class="btn btn-sm btn-outline-secondary"
														type="button"
														:disabled="savingComment"
														@click="closeComment"
													>
														Cancel
													</button>
												</div>
											</div>
										</div>
									</div>

									<!-- Server-derived records — re-derived on read from this
									     recording's own words. Read-only evidence: no row id, no
									     ✎ Correct / 💬 Comment, nothing clickable. -->
									<div
										v-if="transcript.derivedExtractions.length > 0"
										class="derived-section mt-3"
									>
										<div class="derived-heading">Server-derived</div>
										<div class="derived-note text-muted small">
											Rebuilt from this recording's own words on every read —
											read-only evidence rather than reviewable records.
										</div>
										<div
											v-for="(
												extraction, index
											) in transcript.derivedExtractions"
											:key="index"
											class="card extraction-card derived-card mb-2"
										>
											<div class="card-header bg-white py-2">
												<div class="extraction-title">
													<span class="extraction-kind">
														{{ extraction.label }}
													</span>
													<span class="extraction-summary">
														{{ extraction.summary }}
													</span>
												</div>
											</div>
											<div class="card-body py-2">
												<PropertyList :entries="extraction.fields" />
											</div>
										</div>
									</div>
								</div>

								<!-- NLU LAYERS — the device's derived-layer verdict, the drift
								     evidence, and the device's session tree. Read-only: the
								     tree is collapsed by default because the verdict is the
								     review surface and the tree only evidences it. -->
								<div class="mt-3">
									<div class="section-title">NLU LAYERS</div>
									<div v-if="!layers" class="text-muted small">
										No NLU layer snapshot for this recording.
									</div>
									<div v-else class="card layers-card">
										<div class="card-body py-2">
											<div class="layers-header">
												<span
													class="badge"
													:class="layersStatusBadgeClass(layers.status)"
												>
													{{ layers.status }}
												</span>
												<span class="layers-version"
													>v{{ layers.version }}</span
												>
												<span
													class="layers-vocabulary"
													:title="
														'Device vocabulary digest: ' +
														layers.vocabularyVersion
													"
												>
													{{ layers.vocabularyVersion }}
												</span>
												<span class="layers-derived">
													Derived
													<TimeRelativeWithTooltip :time="layers.derivedAt" />
												</span>
											</div>
											<div
												v-if="layers.driftSummary"
												class="layers-drift-summary small"
											>
												{{ layers.driftSummary }}
											</div>
											<div
												v-for="drift in layers.drift"
												:key="drift.layer"
												class="layers-drift small text-muted"
											>
												{{ drift.layer }} — {{ drift.detail }}
											</div>
											<details
												v-if="layersTreeRows.length > 0"
												class="layers-tree"
											>
												<summary>{{ layersTreeSummary }}</summary>
												<div
													v-for="row in layersTreeRows"
													:key="row.node.id"
													class="layers-tree-row"
													:style="layersTreeRowIndent(row)"
												>
													<div class="layers-tree-node">
														<span class="layers-tree-kind">{{
															row.node.kind
														}}</span>
														<span class="layers-tree-label">{{
															row.node.label
														}}</span>
														<span class="layers-tree-state">{{
															row.node.state
														}}</span>
														<span class="layers-tree-span">
															[{{ row.node.startWord }}–{{ row.node.endWord }}]
														</span>
													</div>
													<div
														v-for="entry in row.attributes"
														:key="entry.attribute.position"
														class="layers-tree-attribute small text-muted"
													>
														<span>{{ entry.attribute.label }}</span>
														<template v-if="entry.correction">
															<span
																class="layers-correction-badge"
																title="Corrected by the technician on the device"
																>✎ corrected</span
															>
															<span class="layers-corrected-value">{{
																correctionSideValue(
																	entry.correction.corrected,
																) ?? entry.correction.corrected.state
															}}</span>
															<s class="line-original">{{
																correctionSideValue(
																	entry.correction.original,
																) ?? entry.correction.original.state
															}}</s>
														</template>
														<template v-else>
															<span>{{ entry.attribute.state }}</span>
															<span
																v-if="layersAttributeValue(entry.attribute)"
															>
																{{ layersAttributeValue(entry.attribute) }}
															</span>
														</template>
														<span class="layers-tree-span">
															[{{ entry.attribute.startWord }}–{{
																entry.attribute.endWord
															}}]
														</span>
													</div>
												</div>
											</details>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Teleport>

				<!-- PHOTO VIEW (inline) — opened from a 📷 track marker -->
				<div v-if="selectedPhoto" class="photo-view mt-3">
					<img
						:src="selectedPhoto.thumbnailUrl"
						:alt="selectedPhoto.id"
						class="photo-view-img"
					/>
					<div class="photo-view-meta">
						<strong>{{ selectedPhoto?.id }}</strong>
						·
						{{ formatTranscriptDuration(selectedPhoto?.timestamp ?? 0) }} ·
						{{ selectedPhoto?.location.latitude.toFixed(4) }},
						{{ selectedPhoto?.location.longitude.toFixed(4) }}
						<span
							v-if="photoTranscriptSegment(selectedPhoto)"
							class="photo-segment"
						>
							· "{{ photoTranscriptSegment(selectedPhoto) }}"
						</span>
					</div>
					<button
						class="btn btn-sm btn-outline-secondary"
						@click="selectedPhoto = undefined"
					>
						Close
					</button>
				</div>

				<!-- DERIVED RESOURCES — the ontology records this note version's
				     derivation created or matched. Only site/feature/inspection/
				     treatment have a review page; the other kinds render as plain
				     text rather than a broken link. -->
				<div class="mt-3">
					<div class="section-title">RESOURCES</div>
					<div v-if="derivedResources.length === 0" class="text-muted small">
						No derived resources for this note.
					</div>
					<div v-else class="derived-resource-list">
						<div
							v-for="resource in derivedResources"
							:key="`${resource.kind}:${resource.id}:${resource.role}`"
							class="derived-resource-row"
						>
							<template
								v-if="derivedResourceReviewPath(resource.kind, resource.id)"
							>
								<RouterLink
									class="derived-resource-link"
									:to="derivedResourceReviewPath(resource.kind, resource.id)!"
								>
									{{ derivedResourceLabel(resource.kind) }}
								</RouterLink>
							</template>
							<span v-else class="derived-resource-kind">
								{{ derivedResourceLabel(resource.kind) }}
							</span>
							<span class="derived-resource-id">#{{ resource.id }}</span>
							<span
								class="badge"
								:class="derivedResourceRoleBadgeClass(resource.role)"
							>
								{{ resource.role }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import PropertyList from "@/components/ReviewTranscriptPropertyList.vue";
import ReviewTranscriptToolbar from "@/components/ReviewTranscriptToolbar.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import { apiClient } from "@/client";
import {
	clampSeekTime,
	correctionReviewPayload,
	derivedResourceReviewPath,
	editableFieldValues,
	extractionCorrectionPayload,
	formatTranscriptDuration,
	layersStatusBadgeClass,
	lineIndexAtTime,
	playbackPosition,
	reviewObjectPayload,
	toTranscriptLineView,
	transcriptClarifyPayload,
	transcriptEditPayload,
	transcriptStatusPayload,
	transcriptTitle,
	type TranscriptLineView,
} from "@/lib/transcript";
import type {
	TranscriptDerivedResource,
	TranscriptExtraction,
	TranscriptFlagKind,
	TranscriptLayersAttribute,
	TranscriptLayersCorrection,
	TranscriptLayersCorrectionSide,
	TranscriptLayersNode,
	TranscriptLocationPoint,
	TranscriptNote,
	TranscriptPhoto,
	TranscriptReviewStatus,
} from "@/type/api";

interface Props {
	transcript: TranscriptNote | undefined;
	/** Detail fetch in flight (no note rendered yet). */
	loading?: boolean;
	/** Detail fetch failed. */
	error?: string | null;
}
interface Emits {
	/** A review mutation was accepted — reload the note + queue. */
	"note-changed": [];
	/** A timeline location dot was clicked — recenter the page map on it. */
	"focus-location": [point: TranscriptLocationPoint];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const transcript = computed(() => props.transcript);

const emptyMessage = computed(() => {
	if (props.error) return props.error;
	if (props.loading) return "Loading transcript…";
	return "Select a transcript from the queue";
});

/** Whether the extraction + NLU layers modal is open. */
const analysisOpen = ref(false);

/**
 * The device's derived-layer verdict for the open note, when the version sent
 * one. Null on queue rows, on notes that predate the layers table, and on
 * versions whose device shipped no snapshot — the section then reads as a
 * muted empty line rather than an error.
 */
const layers = computed(() => transcript.value?.layers ?? null);

/**
 * The ontology resources this note version's derivation created or matched —
 * detail-only, so `[]` on queue rows. Defensive `?? []`: rows loaded from a
 * cache written before the field existed carry no `derivedResources`.
 */
const derivedResources = computed(
	() => transcript.value?.derivedResources ?? [],
);

/**
 * One attribute of a tree node paired with the correction that supersedes it
 * (null when the technician left the engine's reading alone).
 */
interface LayersAttributeRow {
	attribute: TranscriptLayersAttribute;
	correction: TranscriptLayersCorrection | null;
}

/** One render row of the device's session tree, flattened for the template. */
interface LayersTreeRow {
	node: TranscriptLayersNode;
	/** Nesting level (0 for roots) — drives the row's indent. */
	depth: number;
	/** The node's attributes, each with its correction resolved up front. */
	attributes: LayersAttributeRow[];
}

/**
 * Flatten the nested device tree into render rows, depth-first in the stored
 * sibling order, carrying an explicit depth so the template can indent rows
 * without recursing (Vue templates cannot). The server nests `children` for
 * us; the walk only re-attaches the level. Each row's attributes resolve their
 * correction by (node id, attribute) here, so the template stays a lookup-free
 * render.
 */
const layersTreeRows = computed<LayersTreeRow[]>(() => {
	const rows: LayersTreeRow[] = [];
	const walk = (nodes: readonly TranscriptLayersNode[], depth: number) => {
		for (const node of nodes) {
			rows.push({
				node,
				depth,
				attributes: node.attributes.map((attribute) => ({
					attribute,
					correction: layersCorrectionFor(node.id, attribute.attribute),
				})),
			});
			walk(node.children, depth + 1);
		}
	};
	walk(layers.value?.nodes ?? [], 0);
	return rows;
});

/** Summary of the collapsed tree toggle, e.g. "Device session tree (5 nodes)". */
const layersTreeSummary = computed(
	() => `Device session tree (${layersTreeRows.value.length} nodes)`,
);

/** Indent one tree row by its nesting level (roots sit flush left). */
const layersTreeRowIndent = (row: LayersTreeRow): { paddingLeft: string } => ({
	paddingLeft: `calc(${row.depth} * 1.1rem)`,
});

/**
 * The captured value of one layer attribute in whichever shape the device
 * used — a value label (with the raw value beside it when they differ), free
 * text, or a measured count with its unit. Null when the attribute is
 * state-only; the caller renders the state alone then.
 */
const layersAttributeValue = (
	attribute: TranscriptLayersAttribute,
): string | null => {
	if (attribute.valueLabel !== null) {
		return attribute.value !== null
			? `${attribute.valueLabel} (${attribute.value})`
			: attribute.valueLabel;
	}
	if (attribute.value !== null) return attribute.value;
	if (attribute.text !== null) return attribute.text;
	if (attribute.count !== null) {
		return attribute.unit !== null
			? `${attribute.count} ${attribute.unit}`
			: String(attribute.count);
	}
	return null;
};

/**
 * The correction to one node attribute on this note version, keyed
 * (nodeID, attribute) — the identity the device writes. Corrections are
 * detail-only (like `layers` itself); null whenever the technician left the
 * engine's reading alone, or the row belongs to another version.
 */
const layersCorrectionFor = (
	nodeID: string,
	attribute: string,
): TranscriptLayersCorrection | null =>
	layers.value?.corrections.find(
		(correction) =>
			correction.nodeID === nodeID && correction.attribute === attribute,
	) ?? null;

/**
 * The reading of one correction side in whichever shape the device stored it —
 * the mirror of `layersAttributeValue` above, except the value columns are
 * plain strings on this wire ("" = unset) and a corrected size arrives in its
 * own typed `measurement` (its value plus unit) rather than as a `count`. Null
 * when the side is state-only; the caller renders the state alone then.
 */
const correctionSideValue = (
	side: TranscriptLayersCorrectionSide,
): string | null => {
	if (side.valueLabel !== "") {
		return side.value !== ""
			? `${side.valueLabel} (${side.value})`
			: side.valueLabel;
	}
	if (side.value !== "") return side.value;
	if (side.text !== "") return side.text;
	if (side.count !== null) {
		return side.unit !== "" ? `${side.count} ${side.unit}` : String(side.count);
	}
	if (side.measurement !== null) {
		return side.measurement.unit !== ""
			? `${side.measurement.value} ${side.measurement.unit}`
			: side.measurement.value;
	}
	return null;
};

/* ── Real playback: a hidden <audio> fed GET /api/transcript/{id}/audio. ── */

const audioEl = ref<HTMLAudioElement | null>(null);
const playbackTime = ref(0);
const isPlaying = ref(false);
const audioDuration = ref<number | undefined>(undefined);
const audioError = ref<string | null>(null);

const audioUrl = computed(() => {
	const note = transcript.value;
	return note ? `/api/transcript/${encodeURIComponent(note.id)}/audio` : "";
});

/** Duration to drive the bar/track: audio metadata once loaded, else note. */
const playerDuration = computed(() => {
	const fromAudio = audioDuration.value;
	if (fromAudio !== undefined && fromAudio > 0) return fromAudio;
	const note = transcript.value;
	const fromNote = note?.durationSeconds ?? 0;
	return fromNote > 0 ? fromNote : 0;
});

const onAudioTime = () => {
	const el = audioEl.value;
	if (el) playbackTime.value = el.currentTime;
};

const onAudioMetadata = () => {
	const el = audioEl.value;
	if (!el) return;
	const duration = el.duration;
	audioDuration.value =
		Number.isFinite(duration) && duration > 0 ? duration : undefined;
};

const onAudioEnded = () => {
	isPlaying.value = false;
	playbackTime.value = playerDuration.value;
};

const onAudioError = () => {
	isPlaying.value = false;
	audioError.value =
		"The recording could not be loaded (missing file or the audio endpoint is unreachable).";
};

const retryAudio = () => {
	audioError.value = null;
	audioEl.value?.load();
};

const togglePlayback = async () => {
	const el = audioEl.value;
	if (!el || audioError.value) return;
	if (el.paused) {
		// Replay from the start when the playhead sits at the end.
		if (
			playerDuration.value > 0 &&
			el.currentTime >= playerDuration.value - 0.05
		) {
			el.currentTime = 0;
		}
		try {
			await el.play();
		} catch {
			audioError.value = "Playback could not start for this recording.";
		}
	} else {
		el.pause();
	}
};

/** Seek the <audio> element to a recording time (clamped). */
const seekTo = (time: number) => {
	const el = audioEl.value;
	const duration = playerDuration.value;
	if (!el || audioError.value || duration <= 0) return;
	el.currentTime = clampSeekTime(time, duration);
	playbackTime.value = el.currentTime;
};

/* ── Audio controls: speed + volume (wired to the audio element). ── */

const playbackSpeeds = [0.5, 0.75, 1, 1.5, 2];
const playbackSpeed = ref(1);
watch(playbackSpeed, (speed) => {
	if (audioEl.value) audioEl.value.playbackRate = speed;
});

const volume = ref(1);
const muted = ref(false);

/** What actually plays: 0 when muted. */
const effectiveVolume = computed(() => (muted.value ? 0 : volume.value));
watch(effectiveVolume, (level) => {
	if (audioEl.value) audioEl.value.volume = level;
});

const volumeIcon = computed(() => {
	const level = effectiveVolume.value;
	if (level === 0) return "bi-volume-mute-fill";
	if (level < 0.5) return "bi-volume-down";
	return "bi-volume-up";
});

const toggleMute = () => {
	muted.value = !muted.value;
};

/** Set the volume (clamped); dragging the slider up while muted unmutes. */
const setVolume = (value: number) => {
	volume.value = Math.min(Math.max(value, 0), 1);
	if (muted.value && volume.value > 0) muted.value = false;
};

/* ── Scrubbing: click/drag on the track seeks the audio element. ── */

const trackEl = ref<HTMLElement | null>(null);
let scrubbing = false;
let resumeAfterScrub = false;

const timeFromEvent = (event: PointerEvent): number => {
	const el = trackEl.value;
	const duration = playerDuration.value;
	if (!el || duration <= 0) return 0;
	const rect = el.getBoundingClientRect();
	const ratio = Math.min(
		Math.max((event.clientX - rect.left) / rect.width, 0),
		1,
	);
	return ratio * duration;
};

const startScrub = (event: PointerEvent) => {
	if (!transcript.value || audioError.value) return;
	resumeAfterScrub = isPlaying.value;
	audioEl.value?.pause();
	scrubbing = true;
	trackEl.value?.setPointerCapture(event.pointerId);
	seekTo(timeFromEvent(event));
};

const scrubMove = (event: PointerEvent) => {
	if (!scrubbing) return;
	seekTo(timeFromEvent(event));
};

const endScrub = () => {
	if (!scrubbing) return;
	scrubbing = false;
	if (resumeAfterScrub) void audioEl.value?.play();
};

/* ── Current-word highlight + line sync (pure helpers from ts/lib). ── */

const activeLineIndex = computed<number | undefined>(() => {
	const note = transcript.value;
	if (!note) return undefined;
	return lineIndexAtTime(note.transcript, playbackTime.value);
});

const activeWord = computed(() => {
	const note = transcript.value;
	if (!note) return undefined;
	return playbackPosition(note.transcript, playbackTime.value);
});

const isPlaybackLine = (index: number): boolean =>
	activeLineIndex.value === index || activeWord.value?.lineIndex === index;

const isActiveWord = (index: number, wordIndex: number): boolean =>
	activeWord.value?.lineIndex === index &&
	activeWord.value?.wordIndex === wordIndex;

/* ── Inline transcript-line editing → PUT …/transcript (non-destructive). ── */

const transcriptLineViews = computed<TranscriptLineView[]>(() => {
	const note = transcript.value;
	if (!note) return [];
	return note.transcript.map(toTranscriptLineView);
});

const editingLineIndex = ref<number | undefined>(undefined);
const editDraft = ref("");
const editInput = ref<HTMLTextAreaElement | null>(null);
const savingLine = ref(false);

// Function ref: the textarea renders inside the line v-for, where a plain
// `ref="…"` would collect into an array instead of the element itself.
const setEditInput = (el: unknown) => {
	editInput.value = el as HTMLTextAreaElement | null;
};

const lineTitle = (
	lineView: TranscriptLineView,
	index: number,
): string | undefined => {
	if (editingLineIndex.value === index) return undefined;
	// Synthesized legacy lines (no server line row) are read-only evidence;
	// clicking them seeks instead of editing.
	return lineView.id === null
		? "Click to seek (read-only server transcript)"
		: "Click to edit";
};

/** Begin inline editing (editable lines) or seek (read-only legacy lines). */
const startLineEdit = (index: number) => {
	if (editingLineIndex.value !== undefined || savingLine.value) return;
	const line = transcript.value?.transcript[index];
	if (!line) return;
	if (line.id === null) {
		seekTo(line.startSeconds);
		return;
	}
	closeCorrection();
	closeComment();
	editingLineIndex.value = index;
	editDraft.value = line.text;
	void nextTick(() => editInput.value?.focus());
};

/** Save the inline edit (Enter) → PUT …/transcript, then reload the note. */
const saveLineEdit = async (index: number) => {
	if (editingLineIndex.value !== index) return;
	const note = transcript.value;
	const line = note?.transcript[index];
	if (!note || !line || line.id === null) return;
	const draft = editDraft.value.trim();
	if (!draft || draft === line.text) {
		cancelLineEdit();
		return;
	}
	savingLine.value = true;
	mutationError.value = null;
	try {
		await apiClient.JSONPut(
			`/api/transcript/${note.id}/transcript`,
			transcriptEditPayload(line.id, draft),
		);
		editingLineIndex.value = undefined;
		editDraft.value = "";
		emit("note-changed");
	} catch (error) {
		mutationError.value = `Save failed — ${messageOf(error)}`;
		// Keep the editor open so the reviewer can retry or Esc out.
	} finally {
		savingLine.value = false;
	}
};

/** Cancel the inline edit (Esc). */
const cancelLineEdit = () => {
	if (savingLine.value) return;
	editingLineIndex.value = undefined;
	editDraft.value = "";
};

/* ── Extraction corrections → POST …/extraction (supersede). ── */

const correctingExtractionId = ref<string | undefined>(undefined);
const correctionSummary = ref("");
const correctionValues = ref<Map<string, string>>(new Map());
const savingExtraction = ref(false);

const openCorrection = (extraction: TranscriptExtraction) => {
	closeComment();
	cancelLineEdit();
	correctingExtractionId.value = extraction.id;
	correctionSummary.value = extraction.summary;
	correctionValues.value = editableFieldValues(extraction.fields);
};

const correctionFieldValue = (key: string): string =>
	correctionValues.value.get(key) ?? "";

const setCorrectionFieldValue = (key: string, value: string) => {
	correctionValues.value.set(key, value);
};

const closeCorrection = () => {
	if (savingExtraction.value) return;
	correctingExtractionId.value = undefined;
};

const saveCorrection = async (extraction: TranscriptExtraction) => {
	const note = transcript.value;
	if (!note || savingExtraction.value) return;
	savingExtraction.value = true;
	mutationError.value = null;
	mutationNotice.value = null;
	try {
		const summary = correctionSummary.value.trim() || extraction.summary;
		const request = extractionCorrectionPayload(
			extraction,
			summary,
			correctionValues.value,
		);
		const created = await apiClient.JSONPost<TranscriptExtraction>(
			`/api/transcript/${note.id}/extraction`,
			request,
		);
		// Record the correction as a Review linking the superseded and the
		// replacement record (best effort — the correction itself is saved).
		try {
			await apiClient.JSONPost(
				"/api/review",
				correctionReviewPayload(
					extraction.kind,
					extraction.id,
					created.id,
					`Corrected ${extraction.kind} ${extraction.id} → ${created.id}`,
				),
			);
		} catch (error) {
			mutationNotice.value = `Correction saved; linking review failed (${messageOf(error)})`;
		}
		closeCorrection();
		emit("note-changed");
	} catch (error) {
		mutationError.value = `Correction failed — ${messageOf(error)}`;
	} finally {
		savingExtraction.value = false;
	}
};

/* ── Extraction comments → POST /api/review. ── */

const commentingExtractionId = ref<string | undefined>(undefined);
const commentDraft = ref("");
const savingComment = ref(false);

const openComment = (extraction: TranscriptExtraction) => {
	closeCorrection();
	cancelLineEdit();
	commentingExtractionId.value = extraction.id;
	commentDraft.value = "";
};

const closeComment = () => {
	if (savingComment.value) return;
	commentingExtractionId.value = undefined;
	commentDraft.value = "";
};

const saveComment = async (extraction: TranscriptExtraction) => {
	const draft = commentDraft.value.trim();
	if (!draft || savingComment.value) return;
	savingComment.value = true;
	mutationError.value = null;
	mutationNotice.value = null;
	try {
		await apiClient.JSONPost(
			"/api/review",
			reviewObjectPayload(extraction.kind, extraction.id, draft),
		);
		closeComment();
		mutationNotice.value = "Comment recorded.";
	} catch (error) {
		mutationError.value = `Comment failed — ${messageOf(error)}`;
	} finally {
		savingComment.value = false;
	}
};

/* ── Note-level actions (toolbar): comment / clarify / status advance. ── */

const savingStatus = ref(false);
const savingClarify = ref(false);
const busy = computed(
	() =>
		savingLine.value ||
		savingExtraction.value ||
		savingComment.value ||
		savingStatus.value ||
		savingClarify.value,
);

/** Toolbar Comment — attach a data-quality review to the whole note. */
const commentOnNote = async (findings: string) => {
	const note = transcript.value;
	if (!note) return;
	mutationError.value = null;
	mutationNotice.value = null;
	try {
		await apiClient.JSONPost(
			"/api/review",
			reviewObjectPayload("transcript", note.id, findings),
		);
		mutationNotice.value = "Comment recorded.";
	} catch (error) {
		mutationError.value = `Comment failed — ${messageOf(error)}`;
	}
};

/** Toolbar Clarify — POST …/clarify; the note enters Awaiting Clarification. */
const clarifyNote = async (question: string) => {
	const note = transcript.value;
	if (!note || savingClarify.value) return;
	savingClarify.value = true;
	mutationError.value = null;
	try {
		await apiClient.JSONPost(
			`/api/transcript/${note.id}/clarify`,
			transcriptClarifyPayload(question),
		);
		emit("note-changed");
	} catch (error) {
		mutationError.value = `Clarify failed — ${messageOf(error)}`;
	} finally {
		savingClarify.value = false;
	}
};

/** Toolbar status select — PUT …/status (review lifecycle transition). */
const updateStatus = async (status: TranscriptReviewStatus) => {
	const note = transcript.value;
	if (!note || status === note.status || savingStatus.value) return;
	savingStatus.value = true;
	mutationError.value = null;
	try {
		await apiClient.JSONPut(
			`/api/transcript/${note.id}/status`,
			transcriptStatusPayload(status),
		);
		emit("note-changed");
	} catch (error) {
		mutationError.value = `Status update failed — ${messageOf(error)}`;
	} finally {
		savingStatus.value = false;
	}
};

/* ── Timeline events (photos + location points) on the track. ── */

/** One event on the review timeline: a location point or an in-recording photo. */
interface TimelineEvent {
	time: number;
	kind: "location" | "photo";
	point?: TranscriptLocationPoint;
	photo?: TranscriptPhoto;
}

/** Location points + photos merged and ordered by recording time. */
const timelineEvents = computed<TimelineEvent[]>(() => {
	const note = transcript.value;
	if (!note) return [];
	const events: TimelineEvent[] = [
		...note.locationStream.map((point) => ({
			time: point.timeOffsetSeconds ?? 0,
			kind: "location" as const,
			point,
		})),
		...note.photos.map((photo) => ({
			time: photo.timestamp,
			kind: "photo" as const,
			photo,
		})),
	];
	return events.sort((a, b) => a.time - b.time);
});

/** Left offset (%) of a marker on the track at the given recording time. */
const markerLeft = (time: number): string => {
	const duration = playerDuration.value;
	if (duration <= 0) return "0%";
	const ratio = Math.min(Math.max(time / duration, 0), 1);
	return `${(ratio * 100).toFixed(2)}%`;
};

const markerTitle = (event: TimelineEvent): string => {
	if (event.kind === "photo") {
		const photo = event.photo;
		return photo
			? `View photo ${photo.id} · ${formatTranscriptDuration(photo.timestamp)}`
			: "Photo";
	}
	const point = event.point;
	return point
		? `location · ${point.distanceMeters ?? "—"} m · ${formatTranscriptDuration(
				point.timeOffsetSeconds ?? 0,
			)}`
		: "Location";
};

/** Open the photo view for a 📷 marker; a ● location dot recenters the page
 *  map on that point instead. */
const openTimelineEvent = (event: TimelineEvent) => {
	if (event.kind === "photo") {
		selectedPhoto.value = event.photo;
	} else if (event.point) {
		emit("focus-location", event.point);
	}
};

/* ── Photo view. ── */

const selectedPhoto = ref<TranscriptPhoto | undefined>(undefined);

/** Transcript line containing the photo's timestamp, if any. */
const photoTranscriptSegment = (
	photo: TranscriptPhoto | undefined,
): string | undefined => {
	if (!photo) return undefined;
	const lines = transcript.value?.transcript ?? [];
	let segment: string | undefined;
	for (const line of lines) {
		if (line.startSeconds <= photo.timestamp) segment = line.text;
		else break;
	}
	return segment;
};

/* ── Playhead position on the track. ── */

const playheadPercent = computed<string>(() => {
	const duration = playerDuration.value;
	if (duration <= 0) return "0%";
	const ratio = Math.min(Math.max(playbackTime.value / duration, 0), 1);
	return `${(ratio * 100).toFixed(2)}%`;
});

/* ── Status/feedback state. ── */

const mutationError = ref<string | null>(null);
const mutationNotice = ref<string | null>(null);

const messageOf = (error: unknown): string => {
	if (error instanceof Error && error.message) return error.message;
	return "unknown error";
};

/* ── Reset per transcript (note id change, not same-note reloads). ── */

watch(
	() => props.transcript?.id,
	() => {
		const el = audioEl.value;
		if (el) {
			el.pause();
			el.currentTime = 0;
			// Re-apply rate/volume after the src change resets the element.
			el.playbackRate = playbackSpeed.value;
			el.volume = effectiveVolume.value;
		}
		isPlaying.value = false;
		playbackTime.value = 0;
		audioDuration.value = undefined;
		audioError.value = null;
		scrubbing = false;
		// Force-clear all editor state (no save-in-flight guards here).
		savingLine.value = false;
		savingExtraction.value = false;
		savingComment.value = false;
		savingStatus.value = false;
		savingClarify.value = false;
		editingLineIndex.value = undefined;
		editDraft.value = "";
		correctingExtractionId.value = undefined;
		commentingExtractionId.value = undefined;
		commentDraft.value = "";
		selectedPhoto.value = undefined;
		analysisOpen.value = false;
		mutationError.value = null;
		mutationNotice.value = null;
	},
);

onBeforeUnmount(() => {
	audioEl.value?.pause();
});

const formatTimestamp = (timestamp: string): string => {
	const date = new Date(timestamp);
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, "0");
	const day = String(date.getUTCDate()).padStart(2, "0");
	const hours = String(date.getUTCHours()).padStart(2, "0");
	const minutes = String(date.getUTCMinutes()).padStart(2, "0");
	return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const flagBadgeClass = (kind: TranscriptFlagKind): string => {
	// System-tagged items (anomaly, out-of-recommendation) use the warning
	// color; reviewer-state flags use a distinct informational style.
	if (kind === "system") return "bg-warning text-dark";
	return "bg-primary text-white";
};

const statusBadgeClass = (status: TranscriptReviewStatus): string => {
	switch (status) {
		case "Recorded":
			return "bg-secondary";
		case "In Review":
			return "bg-info text-dark";
		case "Awaiting Clarification":
			return "bg-warning text-dark";
		case "Superseded":
			return "bg-dark";
		case "Closed":
			return "bg-success";
	}
};

/** Human labels for the ontology kinds a note's derivation can name. */
const DERIVED_RESOURCE_LABELS: Record<string, string> = {
	site: "Site",
	feature: "Feature",
	inspection: "Inspection",
	treatment: "Treatment",
	observation: "Observation",
	assignment: "Assignment",
	facilitator: "Facilitator",
	access_constraint: "Access constraint",
	safety_constraint: "Safety constraint",
};

/**
 * Label for a derived-resource kind. Unknown kinds (a newer server naming a
 * resource this build has not heard of) fall back to the underscored name
 * spaced out, so the row still reads.
 */
const derivedResourceLabel = (kind: string): string =>
	DERIVED_RESOURCE_LABELS[kind] ?? kind.replace(/_/g, " ");

/** Badge class for a derived resource's role: created vs matched. */
const derivedResourceRoleBadgeClass = (
	role: TranscriptDerivedResource["role"],
): string => (role === "created" ? "bg-success" : "bg-info text-dark");
</script>
